/**
 * FAQFullScreen — full-height overlay for FAQ.
 * Same pattern as SponsorsFullScreen: fixed overlay on top of map.
 */

import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/pro-solid-svg-icons/faCircleQuestion";
import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons/faArrowLeft";
import type { FAQConfig } from "@wondermaps/db/schema";
import { getTranslatedValue, type NameTranslations } from "@wondermaps/db/client";
import { FAQBody } from "./FAQBottomSheet";

export interface FAQFullScreenProps {
  faqConfig: FAQConfig;
  lang: string;
  onClose: () => void;
}

export function FAQFullScreen({
  faqConfig,
  lang,
  onClose,
}: FAQFullScreenProps) {
  const { t } = useTranslation("map");

  const faqTitle = getTranslatedValue(
    faqConfig.title || t("menu.faq"),
    faqConfig.titleTranslations as NameTranslations,
    lang,
  );

  return (
    <div className="fixed inset-0 z-30 bg-background flex flex-col" role="dialog" aria-modal="true" aria-label={faqTitle}>
      {/* Header */}
      <div className="flex items-center gap-2 border-b px-4 py-3 shrink-0">
        <button
          type="button"
          onClick={onClose}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center shrink-0"
          aria-label={t("faq.close")}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5" />
        </button>
        <FontAwesomeIcon icon={faCircleQuestion} className="h-5 w-5 text-muted-foreground" />
        <h2 className="font-semibold text-lg flex-1">{faqTitle}</h2>
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <FAQBody faqConfig={faqConfig} lang={lang} />
      </div>
    </div>
  );
}
